import { Router } from '@angular/router';
import { of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { PermissionService } from '../permissions/permission.service';
import { DatadogService } from '@app/shared/services/datadog/datadog.service';
import { SessionService } from '../session/session.service';
import * as i0 from "@angular/core";
import * as i1 from "../permissions/permission.service";
import * as i2 from "@angular/router";
import * as i3 from "../../shared/services/datadog/datadog.service";
import * as i4 from "../session/session.service";
/**
 * Guard para validacao de permissoes de menu antes da navegacao
 * Implementa logging de tentativas de acesso negado
 */
var PermissionGuard = /** @class */ (function () {
    function PermissionGuard(permissionService, router, datadogService, sessionService) {
        this.permissionService = permissionService;
        this.router = router;
        this.datadogService = datadogService;
        this.sessionService = sessionService;
        this.redirectInProgress = false;
        this.lastRedirectTime = 0;
        this.MIN_REDIRECT_INTERVAL_MS = 1000;
    }
    PermissionGuard.prototype.canActivate = function (route, state) {
        var _this = this;
        var startTime = performance.now();
        var targetRoute = state.url;
        // Rotas que nao precisam de validacao de permissao
        var publicRoutes = ['/no-permission', '/service-unavailable', '/error', '/browser-warning'];
        if (publicRoutes.some(function (r) { return targetRoute.startsWith(r); })) {
            return true;
        }
        // Tenta carregar permissoes do cache primeiro
        var cachedPermissions = this.permissionService.loadPermissionsFromStorage();
        if (cachedPermissions) {
            return this.validatePermission(targetRoute, startTime);
        }
        // Se nao ha cache, busca do endpoint
        return this.permissionService.getPermissions(true).pipe(switchMap(function () { return of(_this.validatePermissionSync(targetRoute, startTime)); }), catchError(function (error) {
            _this.logAccessDenied(targetRoute, 'endpoint_unavailable', startTime);
            _this.safeNavigate('/service-unavailable');
            return of(false);
        }));
    };
    /**
     * Valida permissao de forma sincrona
     */
    PermissionGuard.prototype.validatePermissionSync = function (route, startTime) {
        var normalizedRoute = route.split(';')[0];
        var hasPermission = this.permissionService.hasPermissionForRoute(normalizedRoute);
        var validationTime = performance.now() - startTime;
        // Log de performance se validacao demorar mais de 500ms
        if (validationTime > 500) {
            this.datadogService.logInfo('Validacao de permissao lenta', {
                route: normalizedRoute,
                validationTimeMs: validationTime
            });
        }
        if (!hasPermission) {
            this.logAccessDenied(normalizedRoute, 'permission_denied', startTime);
            this.safeNavigate('/no-permission', { attemptedRoute: normalizedRoute });
            return false;
        }
        return true;
    };
    /**
     * Valida permissao retornando Observable
     */
    PermissionGuard.prototype.validatePermission = function (route, startTime) {
        return of(this.validatePermissionSync(route, startTime));
    };
    /**
     * Navegacao segura com prevencao de loops
     */
    PermissionGuard.prototype.safeNavigate = function (path, queryParams) {
        var _this = this;
        if (!this.canRedirect()) {
            this.datadogService.sendErrorLogs('Possivel loop de redirecionamento detectado', {
                path: path
            });
            return;
        }
        this.startRedirect();
        var navigationExtras = queryParams ? { queryParams: queryParams } : undefined;
        this.router.navigate([path], navigationExtras).then(function () {
            _this.endRedirect();
        }).catch(function () {
            _this.endRedirect();
        });
    };
    /**
     * Verifica se pode redirecionar (prevencao de loops)
     */
    PermissionGuard.prototype.canRedirect = function () {
        var now = Date.now();
        if (this.redirectInProgress || (now - this.lastRedirectTime) < this.MIN_REDIRECT_INTERVAL_MS) {
            return false;
        }
        return true;
    };
    /**
     * Marca inicio do redirecionamento
     */
    PermissionGuard.prototype.startRedirect = function () {
        this.redirectInProgress = true;
        this.lastRedirectTime = Date.now();
    };
    /**
     * Marca fim do redirecionamento
     */
    PermissionGuard.prototype.endRedirect = function () {
        this.redirectInProgress = false;
    };
    /**
     * Registra log de acesso negado com campos obrigatorios para auditoria
     */
    PermissionGuard.prototype.logAccessDenied = function (route, reason, startTime) {
        var authUser = this.sessionService.getAuthUser();
        var validationTime = performance.now() - startTime;
        var cacheStatus = this.permissionService.getLastCacheStatus();
        var userId = this.permissionService.getCurrentUserId();
        // Log com campos obrigatorios conforme requisito de auditoria
        this.datadogService.logInfo('access_denied', {
            event: 'access_denied',
            timestamp: new Date().toISOString(),
            userId: authUser ? userId : 'unknown',
            businessId: this.extractBusinessId(authUser),
            gestor: authUser ? authUser.gestor : 'unknown',
            attemptedRoute: route,
            requiredPermission: this.extractRequiredPermission(route),
            sessionId: this.getSessionId(),
            userAgent: navigator.userAgent,
            cacheStatus: cacheStatus,
            reason: reason,
            validationTimeMs: validationTime
        });
    };
    /**
     * Extrai businessId do usuario autenticado
     */
    PermissionGuard.prototype.extractBusinessId = function (authUser) {
        if (!authUser)
            return 'unknown';
        return authUser.businessId || authUser.idConexao || authUser.usuario || 'unknown';
    };
    /**
     * Extrai permissao requerida da rota
     */
    PermissionGuard.prototype.extractRequiredPermission = function (route) {
        // Normaliza a rota para identificar a permissao requerida
        var normalizedRoute = route.replace(/^\/+|\/+$/g, '').split('?')[0];
        return normalizedRoute || 'unknown';
    };
    /**
     * Obtem ID da sessao do sessionStorage
     */
    PermissionGuard.prototype.getSessionId = function () {
        try {
            var authUser = this.sessionService.getAuthUser();
            return authUser ? (authUser.idSessao || authUser.idConexao || 'unknown') : 'unknown';
        }
        catch (_a) {
            return 'unknown';
        }
    };
    PermissionGuard.ngInjectableDef = i0.defineInjectable({ factory: function PermissionGuard_Factory() { return new PermissionGuard(i0.inject(i1.PermissionService), i0.inject(i2.Router), i0.inject(i3.DatadogService), i0.inject(i4.SessionService)); }, token: PermissionGuard, providedIn: "root" });
    return PermissionGuard;
}());
export { PermissionGuard };
